const SITE_URL = 'https://cdbattaglia.com';
const DEFAULT_OG_IMAGE = '/og/default.png';
export const PERSON_ID = 'https://cdbattaglia.com/#person';

function resolveUrl(value: string): string {
  if (value.startsWith('http')) return value;
  return `${SITE_URL}${value}`;
}

function readTimeToIsoDuration(readTime?: string): string | undefined {
  const minutes = readTime?.match(/(\d+)/)?.[1];
  return minutes ? `PT${minutes}M` : undefined;
}

interface PageMeta {
  title: string;
  description: string;
  path: string;
  image?: string;
  imageAlt?: string;
  imageWidth?: string;
  imageHeight?: string;
  type?: string;
  canonical?: boolean;
  keywords?: readonly string[];
  robots?: string;
}

interface ArticleMeta extends PageMeta {
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  tags?: string[];
  readTime?: string;
  section?: string;
}

export const PAGE_KEYWORDS = {
  home: [
    'christian battaglia',
    'cdbattags',
    'product engineer',
    'open source',
    'typescript',
    'rust',
    'music technology',
    'product engineering',
  ],
  blog: [
    'blog',
    'software engineering',
    'product engineering',
    'rust',
    'typescript',
    'infrastructure',
    'music technology',
    'open source',
  ],
  prodeng: [
    'product engineering',
    'engineering leadership',
    'uncertainty',
    'decision-making',
    'product management',
    'software engineering',
    'organizational design',
    'trust',
  ],
  projects: [
    'open source',
    'rust',
    'nginx',
    'lua',
    'typescript',
    'music analysis',
    'infrastructure',
    'github',
  ],
  repos: [
    'github',
    'rust',
    'nginx',
    'audio',
    'open source',
    'forks',
    'infrastructure',
    'trading',
  ],
  quotes: [
    'quotes',
    'inspiration',
    'philosophy',
    'engineering',
    'leadership',
    'personal',
    'sticky notes',
    'christian battaglia',
  ],
  uses: [
    'uses',
    'developer setup',
    'macos',
    'nix',
    'cursor',
    'mechanical keyboard',
    'infrastructure',
    'developer tools',
  ],
  about: [
    'christian battaglia',
    'cdbattags',
    'cto',
    'software architect',
    'react',
    'nodejs',
    'python',
    'typescript',
    'aws',
    'kubernetes',
    'openresty',
    'infrastructure as code',
    'music technology',
    'open source',
  ],
  music: [
    'audio analysis',
    'music analysis',
    'librosa',
    'spotify api',
    'tempo detection',
    'key detection',
    'beat tracking',
    'audio visualization',
    'waveform',
    'spectrogram',
    'music technology',
  ],
} as const;

export function createCollectionJsonLd(opts: {
  type?: 'CollectionPage' | 'Blog' | 'WebPage';
  name: string;
  description: string;
  path: string;
  keywords?: readonly string[] | string[];
}) {
  return {
    type: 'application/ld+json',
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': opts.type ?? 'CollectionPage',
      name: opts.name,
      description: opts.description,
      url: resolveUrl(opts.path),
      author: { '@type': 'Person', '@id': PERSON_ID, name: 'Christian Battaglia' },
      keywords: opts.keywords?.length ? [...opts.keywords].join(', ') : undefined,
      inLanguage: 'en-US',
    }),
  };
}

export function createPageMeta(opts: PageMeta) {
  const {
    title,
    description,
    path,
    image = DEFAULT_OG_IMAGE,
    imageAlt = title,
    imageWidth = '1200',
    imageHeight = '630',
    type = 'website',
    canonical = true,
    keywords,
    robots,
  } = opts;

  const url = resolveUrl(path);
  const imageUrl = resolveUrl(image);

  const meta: Array<Record<string, string>> = [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'og:type', content: type },
    { property: 'og:image', content: imageUrl },
    { property: 'og:image:alt', content: imageAlt },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'og:image:width', content: imageWidth },
    { property: 'og:image:height', content: imageHeight },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:image:alt', content: imageAlt },
    ...(keywords?.length ? [{ name: 'keywords', content: keywords.join(', ') }] : []),
    ...(robots ? [{ name: 'robots', content: robots }] : []),
  ];

  const links: Array<Record<string, string>> = [];
  if (canonical) {
    links.push({ rel: 'canonical', href: url });
  }

  return { meta, links };
}

export function createArticleMeta(opts: ArticleMeta) {
  const {
    datePublished,
    dateModified,
    authorName = 'Christian Battaglia',
    tags = [],
    readTime,
    section,
    ...page
  } = opts;

  const base = createPageMeta({
    ...page,
    type: page.type ?? 'article',
    keywords: page.keywords ?? (tags.length ? tags : undefined),
  });
  const url = resolveUrl(page.path);
  const imageUrl = resolveUrl(page.image ?? DEFAULT_OG_IMAGE);
  const timeRequired = readTimeToIsoDuration(readTime);

  const articleMeta = [
    ...base.meta,
    ...(datePublished ? [{ property: 'article:published_time', content: datePublished }] : []),
    ...(dateModified ? [{ property: 'article:modified_time', content: dateModified }] : []),
    { property: 'article:author', content: authorName },
    ...(section ? [{ property: 'article:section', content: section }] : []),
    ...tags.slice(0, 8).map(tag => ({ property: 'article:tag', content: tag })),
  ];

  return {
    meta: articleMeta,
    links: base.links,
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: page.title,
          description: page.description,
          image: imageUrl,
          url,
          mainEntityOfPage: { '@type': 'WebPage', '@id': url },
          datePublished,
          dateModified: dateModified || datePublished,
          author: {
            '@type': 'Person',
            '@id': PERSON_ID,
            name: authorName,
          },
          publisher: {
            '@type': 'Person',
            '@id': PERSON_ID,
            name: 'Christian Battaglia',
          },
          keywords: tags.length ? tags.join(', ') : undefined,
          timeRequired,
          inLanguage: 'en-US',
        }),
      },
    ],
  };
}
